import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@arcadeai/design-system/components/ui/atoms/sheet";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useCreateServer, useUpdateServer } from "@/app/servers/api/hooks";
import { useOAuthAuthorize } from "@/app/servers/hooks/use-oauth-authorize";
import {
  getScopesSupported,
  requiresOAuth,
  type Server,
  type ServerForm as ServerFormType,
} from "@/app/servers/types";
import { ServerForm } from "./form/form";
import { OAuthAuthorizationScreen } from "./oauth/oauth-authorization-screen";

type ServerSheetProps = {
  server?: Server;
  trigger?: React.ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type AuthFlowState = {
  serverId: string;
  scopesSupported?: string[];
};

export const ServerSheet = ({
  server,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ServerSheetProps) => {
  const { tab } = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/servers/",
  });
  const navigate = useNavigate();
  const [isManuallyOpen, setIsManuallyOpen] = useState(false);
  const [authFlow, setAuthFlow] = useState<AuthFlowState | null>(null);
  // null = user hasn't interacted, default to all discovered scopes
  const [userSelectedScopes, setUserSelectedScopes] = useState<string[] | null>(
    null
  );

  const isControlled = controlledOpen !== undefined;

  const isOpen = useMemo(() => {
    if (isControlled) {
      return controlledOpen;
    }
    return (tab === "new" && !server) || isManuallyOpen;
  }, [isControlled, controlledOpen, tab, server, isManuallyOpen]);

  const { mutateAsync: updateServer, isPending: isUpdating } = useUpdateServer({
    id: server?.id ?? "",
  });
  const { mutateAsync: createServer, isPending: isCreating } =
    useCreateServer();

  const isEditing = Boolean(server);
  const isLoading = isUpdating || isCreating;

  const selectedScopes = userSelectedScopes ?? authFlow?.scopesSupported ?? [];

  const navigateToList = useCallback(() => {
    if (tab === "new") {
      navigate({
        to: ".",
        search: (params: Record<string, unknown>) => ({
          ...params,
          tab: "list" as const,
          defaultValues: undefined,
        }),
      });
    }
  }, [tab, navigate]);

  const {
    authorizationUrl,
    authorize,
    reset: resetAuth,
    isPolling,
  } = useOAuthAuthorize({
    serverId: authFlow?.serverId ?? "",
    onAuthComplete: () => {
      if (isControlled) {
        controlledOnOpenChange?.(false);
      } else {
        setIsManuallyOpen(false);
      }
      setAuthFlow(null);
      setUserSelectedScopes(null);
      navigateToList();
    },
  });

  const handleUpdateServer = useCallback(
    async (values: ServerFormType) => {
      await updateServer(values);
      if (isControlled) {
        controlledOnOpenChange?.(false);
      } else {
        setIsManuallyOpen(false);
      }
      navigateToList();
    },
    [updateServer, navigateToList, isControlled, controlledOnOpenChange]
  );

  const resetAndClose = useCallback(() => {
    if (isControlled) {
      controlledOnOpenChange?.(false);
    } else {
      setIsManuallyOpen(false);
    }
    setAuthFlow(null);
    setUserSelectedScopes(null);
    resetAuth();
    navigateToList();
  }, [navigateToList, resetAuth, isControlled, controlledOnOpenChange]);

  const handleCreateServer = useCallback(
    async (values: ServerFormType) => {
      const newServer = await createServer(values);
      const needsOAuth = requiresOAuth(newServer);

      if (!needsOAuth) {
        resetAndClose();
        return;
      }

      const discoveredScopes = getScopesSupported(newServer);

      // Transition to the auth screen so the create form is no longer
      // visible. This prevents duplicate server creation if the user
      // re-submits.
      setAuthFlow({
        serverId: newServer.id,
        scopesSupported: discoveredScopes,
      });
    },
    [createServer, resetAndClose]
  );

  const handleSubmit = useCallback(
    async (values: ServerFormType) => {
      try {
        if (isEditing) {
          await handleUpdateServer(values);
        } else {
          await handleCreateServer(values);
        }
      } catch (_error) {
        // Error is intentionally ignored as it's handled by the mutation
      }
    },
    [isEditing, handleUpdateServer, handleCreateServer]
  );

  const handleScopesChange = useCallback(
    (scopes: string[]) => {
      setUserSelectedScopes(scopes);
      resetAuth();
    },
    [resetAuth]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        if (isControlled) {
          controlledOnOpenChange?.(true);
        } else {
          setIsManuallyOpen(true);
        }
      } else {
        resetAndClose();
      }
    },
    [resetAndClose, isControlled, controlledOnOpenChange]
  );

  return (
    <Sheet onOpenChange={handleOpenChange} open={isOpen}>
      {!!trigger && <SheetTrigger render={trigger} />}
      <SheetContent className="w-full px-2" tabIndex={-1}>
        {!!isOpen && (
          <ScrollArea className="h-full w-full" tabIndex={-1}>
            <SheetHeader>
              <SheetTitle className="mt-2">
                {(() => {
                  if (authFlow) {
                    return "Authorize Server";
                  }
                  if (isEditing) {
                    return "Edit Server";
                  }
                  return "Add Server";
                })()}
              </SheetTitle>
              <SheetDescription>
                {authFlow
                  ? "Complete the authorization process to activate your server."
                  : "Configure settings for your remote MCP or Arcade server."}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-2 px-4">
              {authFlow ? (
                <OAuthAuthorizationScreen
                  authorizationUrl={authorizationUrl}
                  isPolling={isPolling}
                  onAuthorize={authorize}
                  onCompleteLater={resetAndClose}
                  onScopesChange={handleScopesChange}
                  scopesSupported={authFlow.scopesSupported}
                  selectedScopes={selectedScopes}
                />
              ) : (
                <ServerForm
                  isLoading={isLoading}
                  onSubmit={handleSubmit}
                  server={server}
                />
              )}
            </div>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
};
